import { slugify, slugifyProvince } from '@paction-2.0/util';
import { parseLocationSlug } from '@/features/locations';
import {
  normalizeCatalogSearchFilters,
  type CatalogSearchFilters,
  type NormalizedCatalogSearchFilters
} from '@/features/catalog';

export const PUPPY_SEARCH_PAGE_SIZE = 12;
// Deprecated: kept temporarily for backwards-compat with anything that still
// imports it. The map no longer drives the result-list page size — markers
// come from the dedicated `/catalog/providers/markers` endpoint
// (see `loadCatalogProviderMarkers`) and the result list paginates with
// `PUPPY_SEARCH_PAGE_SIZE`.
export const PUPPY_SEARCH_MAP_PAGE_SIZE = PUPPY_SEARCH_PAGE_SIZE;
export type PuppySearchRouteParams = {
  breed?: string;
  province?: string;
  city?: string;
};

export type PuppySearchSearchParams =
  | URLSearchParams
  | Record<string, string | string[] | undefined>;

const truthy = new Set(['true', '1', 'yes']);
const falsy = new Set(['false', '0', 'no']);
const normalizeSearchParams = (
  input?: PuppySearchSearchParams
): URLSearchParams => {
  if (!input) {
    return new URLSearchParams();
  }
  if (input instanceof URLSearchParams) {
    return input;
  }
  const params = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null) {
          params.append(key, entry);
        }
      });
      return;
    }
    params.append(key, value);
  });
  return params;
};
const parseBooleanParam = (value: string | null | undefined) => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (truthy.has(normalized)) return true;
  if (falsy.has(normalized)) return false;
  return undefined;
};
const toSlug = (value?: string | null) => {
  if (!value) return undefined;
  const slug = slugify(value);
  return slug.length > 0 ? slug : undefined;
};
const toProvinceSlug = (value?: string | null) => {
  if (!value) return undefined;
  const slug = slugifyProvince(value);
  return slug.length > 0 ? slug : undefined;
};
const parseNumberParam = (value: string | null | undefined) => {
  if (value === undefined || value === null) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};
const extractRouteFromPathname = (
  pathname?: string
): PuppySearchRouteParams => {
  if (!pathname) return {};
  const segments = pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (segments.length === 0 || segments[0] !== 'puppies') {
    return {};
  }
  if (segments.length === 1) {
    return {};
  }
  const breed = decodeURIComponent(segments[1] ?? '');
  const province =
    segments.length > 2 ? decodeURIComponent(segments[2] ?? '') : undefined;
  const city =
    segments.length > 3 ? decodeURIComponent(segments[3] ?? '') : undefined;
  return { breed, province, city } satisfies PuppySearchRouteParams;
};

export const deriveFiltersFromRoute = (
  route: PuppySearchRouteParams,
  searchParamsInput?: PuppySearchSearchParams
): NormalizedCatalogSearchFilters => {
  const params = normalizeSearchParams(searchParamsInput);
  const searchTerm = params.get('search')?.trim();
  const availableNow = parseBooleanParam(params.get('availableNow'));
  const orderParam = params.get('order');
  const order =
    orderParam === 'distance' || orderParam === 'recent'
      ? orderParam
      : 'staggered';
  let breedSlug = toSlug(route.breed);
  if (!breedSlug) {
    breedSlug = toSlug(params.get('breed'));
  }
  let provinceSlug = toProvinceSlug(route.province);
  if (!provinceSlug) {
    provinceSlug = toProvinceSlug(params.get('province'));
  }
  let citySlug = toSlug(route.city);
  if (!citySlug) {
    citySlug = toSlug(params.get('city'));
  }
  if (route.province && !route.city) {
    const resolvedLocation = parseLocationSlug(route.province);
    if (resolvedLocation?.type === 'city') {
      citySlug = resolvedLocation.city?.slug ?? citySlug;
      provinceSlug = resolvedLocation.province?.slug ?? provinceSlug;
    }
  }
  const limitParam = parseNumberParam(params.get('limit'));
  const pageParam = parseNumberParam(params.get('page'));
  const baseFilters: CatalogSearchFilters = {
    page: pageParam ?? 1,
    limit: limitParam ?? PUPPY_SEARCH_PAGE_SIZE,
    search: searchTerm || undefined,
    breedSlug,
    province: provinceSlug,
    city: citySlug,
    availableNow,
    includeListings: true,
    includeWaitlists: true,
    order
  };
  return normalizeCatalogSearchFilters(baseFilters);
};
export const deriveFiltersFromUrl = (
  pathname: string,
  search: string
): NormalizedCatalogSearchFilters => {
  const route = extractRouteFromPathname(pathname);
  const params = new URLSearchParams(search);
  return deriveFiltersFromRoute(route, params);
};
export const buildUrlFromFilters = (
  input: CatalogSearchFilters
): { pathname: string; search: string } => {
  const filters = normalizeCatalogSearchFilters(input);
  const segments = ['puppies'];
  if (filters.breedSlug) {
    segments.push(filters.breedSlug);
    if (filters.city) {
      segments.push(filters.city);
    } else if (filters.province) {
      segments.push(filters.province);
    }
  }
  const pathname = `/${segments.join('/')}`;
  const params = new URLSearchParams();
  if (filters.search) {
    params.set('search', filters.search);
  }
  if (typeof filters.availableNow === 'boolean') {
    params.set('availableNow', String(filters.availableNow));
  }
  if (!filters.breedSlug && filters.province) {
    params.set('province', filters.province);
  }
  if (!filters.breedSlug && filters.city) {
    params.set('city', filters.city);
  }
  if (filters.order && filters.order !== 'staggered') {
    params.set('order', filters.order);
  }
  return { pathname, search: params.toString() };
};
